import React, { createContext, useState, useEffect, ReactNode, useContext } from 'react';
import { getLicenseCustomization } from '../services/license';  // Corrected import

export interface OauthButton {
    provider: string;
    icon: string;
    displayNameEn: string;
    displayNameZh: string;
}

export interface CustomizationType {
  heroImage: string | null;
  requireStrongPassword?: boolean;
  productName: {
    en: string;
    'zh-CN': string;
  };
  copyright: {
    en: string;
    'zh-CN': string;
  };
  oauthButtons: OauthButton[];
  localLoginForm: {
    locked: boolean;
    message: {
      en: string;
      'zh-CN': string;
    };
    admin?: {
      locked?: boolean;
      message?: {
        en?: string;
        'zh-CN'?: string;
      };
    };
    redirectUrl: string;
  };
  forgotPasswordURL: string;
  features: Record<
    'enableImageGeneration' | 'enableMultimodalModel' | 'displayProductConcept' | 'enableOutlineDocx' | 'enableOutlineFineTuning' | string,
    boolean
  >;
  defaultPreferences: Record<'showOnboardingTips' | string, unknown>;
  limits: {
    concurrentModelRequest: number;
  };
  hints: {
    projectCreating: string;
  };
  trainingMaterialURL?: string;
}

export const defaultConfig: CustomizationType = {
  heroImage: null,
  requireStrongPassword: false,
  productName: {
    en: 'Seeyon Show',
    'zh-CN': '致秀',
  },
  copyright: {
    en: 'Seeyon Internet, all rights reserved',
    'zh-CN': '致远互联 版权所有',
  },
  oauthButtons: [],
  localLoginForm: {
    locked: false,
    message: {
      en: 'Login locked',
      'zh-CN': '登录已锁定',
    },
    admin: undefined,
    redirectUrl: '',
  },
  forgotPasswordURL: '',
  features: {},
  hints: {
    projectCreating:
      '描述您想创建一个什么样的 PPT 演示项目，AI 会根据您的描述引导您完成项目创建。例如：请帮我创建一个关于公司季度业绩报告的演示，或者请帮我创建一个关于大模型原理的学术讲座。',
  },
  limits: {
    concurrentModelRequest: 8,
  },
  trainingMaterialURL: '',
  defaultPreferences: {},
};

export const BrandingContext = createContext<CustomizationType | null>(null);

export const BrandingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [customization, setCustomization] = useState(() => {
    const cached = localStorage.getItem('customization');
    return cached ? JSON.parse(cached) : defaultConfig;
  });

  useEffect(() => {
    getLicenseCustomization()
      .then((data: CustomizationType) => {
        if (data) {
          setCustomization(data);
          localStorage.setItem('customization', JSON.stringify(data));
        }
      })
      .catch((error: Error) => {  
        console.error('Failed to load customization:', error);
      });
  }, []);

  const mergedConfig = { ...defaultConfig, ...customization };
  return (
    <BrandingContext.Provider value={mergedConfig}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};
